import React, { useCallback, useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import {
    DA_ContentBox,
    DA_ContentBoxRef,
} from "@danishagro/shared/src/components/atoms/ContentBox/ContentBox.component";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_UserInformation } from "@danishagro/shared/src/interfaces/userInformation.interface";
import { DA_Container } from "@danishagro/shared/src/components/atoms/Container/Container.component";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { DA_Accordion } from "@danishagro/shared/src/components/molecules/Accordion/Accordion.component";
import { DA_DynamicContent } from "@danishagro/shared/src/components/molecules/DynamicContent/DynamicContent.component";
import { useUserInformationApi } from "@danishagro/shared/src/hooks/api/useUserInformationApi.hook";
import { useDynamicOverlay } from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { _UserEditOverlay } from "../components/UserEditOverlay/UserEditOverlay.component";
import S from "./MyProfileV1.module.scss";

interface V1Props {
    parentVersion?: string;
}

const V1 = (_props: V1Props) => {
    const { getDictionaryString } = useTranslations();
    const { currentCulture } = useAppData();
    const [awaitingResponse, setAwaitingResponse] = useState(false);
    const [userInfoData, setUserInfoData] = useState<DA_UserInformation>();
    const [resetPasswordLink, setResetPasswordLink] = useState<string>();

    const contentBoxRef = useRef<DA_ContentBoxRef>(null);
    const { getUserInfo, getResetPasswordLink } = useUserInformationApi();
    const { showOverlay } = useDynamicOverlay();
    const { isMobileOrTablet } = useScreen();

    const onClickEdit = useCallback(() => {
        showOverlay({
            id: "user-info-edit-overlay",
            title: getDictionaryString("profile.user.edit.title"),
            content: <_UserEditOverlay {...userInfoData} onSuccess={setUserInfoData} />,
        });
    }, [showOverlay, getDictionaryString, userInfoData]);

    useEffect(() => {
        // Get data from API on mount
        setAwaitingResponse(true);
        Promise.all([getUserInfo(), getResetPasswordLink(currentCulture, window.location.href)])
            .then(([userInfoResponse, resetPasswordResponse]) => {
                setUserInfoData(userInfoResponse);
                setResetPasswordLink(resetPasswordResponse.url);
            })
            .catch(() => contentBoxRef.current.alert())
            .finally(() => setAwaitingResponse(false));
    }, [currentCulture, getUserInfo, getResetPasswordLink]);

    const content = (
        <>
            <div className={S.wrapper}>
                <div className={S.grid}>
                    {/* Name */}
                    <span>
                        <div className={S.label}>{getDictionaryString("profile.user.name")}</div>
                        {awaitingResponse ? (
                            <Skeleton width="50%" />
                        ) : (
                            `${userInfoData?.firstName} ${userInfoData?.lastName}`
                        )}
                    </span>

                    {/* Email */}
                    <span>
                        <div className={S.label}>{getDictionaryString("profile.user.email")}</div>
                        {awaitingResponse ? <Skeleton width="50%" /> : userInfoData?.email}
                    </span>

                    {/* Phone */}
                    <span>
                        <div className={S.label}>{getDictionaryString("profile.user.phone")}</div>
                        {awaitingResponse ? <Skeleton width="50%" /> : userInfoData?.phone}
                    </span>

                    {/* Cell phone */}
                    <span>
                        <div className={S.label}>
                            {getDictionaryString("profile.user.cellPhone")}
                        </div>
                        {awaitingResponse ? <Skeleton width="50%" /> : userInfoData?.cellPhone}
                    </span>
                </div>
            </div>

            {/* Edit user info */}
            <DA_Button
                isGhost
                title={getDictionaryString("profile.user.edit")}
                className={S.editProfile}
                onClick={onClickEdit}
                disabled={!userInfoData}
                icon={DA_IconNames.Pencil}
                iconRight={!isMobileOrTablet}
            />
        </>
    );

    const passwordContent = (
        <DA_Button
            isGhost
            title={getDictionaryString("profile.user.changePassword")}
            className={S.changePassword}
            href={resetPasswordLink}
            target="_self"
            conventionalRouting
            icon={DA_IconNames.Pencil}
            iconRight={!isMobileOrTablet}
        />
    );

    return (
        <DA_Container>
            <DA_ContentBox ref={contentBoxRef} loading={awaitingResponse} mediumMargin>
                {isMobileOrTablet ? (
                    <DA_Accordion
                        title={
                            <span className={S.accordionTitle}>
                                {getDictionaryString("profile.user.title")}
                            </span>
                        }
                        showToggleIcons
                        animation
                        showLargeArrow
                        showSmallHeader
                        noPaddingHeader
                        noPaddingContent
                        noBorder
                        className={S.accordion}
                    >
                        <span className={S.description}>
                            {getDictionaryString("profile.user.description")}
                        </span>
                        {content}
                    </DA_Accordion>
                ) : (
                    <DA_DynamicContent
                        title={getDictionaryString("profile.user.title")}
                        text={getDictionaryString("profile.user.description")}
                        content={
                            isMobileOrTablet ? (
                                <DA_Accordion title={getDictionaryString("profile.user.title")}>
                                    {content}
                                </DA_Accordion>
                            ) : (
                                content
                            )
                        }
                    />
                )}
            </DA_ContentBox>

            {/* Change password */}
            <DA_ContentBox ref={contentBoxRef} loading={awaitingResponse} mediumMargin>
                {isMobileOrTablet ? (
                    <DA_Accordion
                        title={
                            <span className={S.accordionTitle}>
                                {getDictionaryString("profile.user.password.title")}
                            </span>
                        }
                        showToggleIcons
                        animation
                        showLargeArrow
                        showSmallHeader
                        noPaddingHeader
                        noPaddingContent
                        noBorder
                        className={S.accordion}
                    >
                        <span className={S.description}>
                            {getDictionaryString("profile.user.password.description")}
                        </span>
                        {passwordContent}
                    </DA_Accordion>
                ) : (
                    <DA_DynamicContent
                        title={getDictionaryString("profile.user.password.title")}
                        text={getDictionaryString("profile.user.password.description")}
                        content={passwordContent}
                    />
                )}
            </DA_ContentBox>
        </DA_Container>
    );
};

export default V1;
